import * as React from "react";
import {useEffect} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {CenterBox} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, display, flex, flexDirection, mb, mt, p, pb, ph, position, pv, textAlign, w100} from "@pg-design/helpers-css";
import {AdditionalAreaIcon, AreaIcon, CalendarCheckIcon, KitchenIcon, RoomsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {SliderGallery, SliderGalleryIcon} from "@pg-design/slider-gallery-module";
import {Text} from "@pg-design/text-module";
import {RequestState, useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, roomsFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../../app/rp_reducer";
import {InfoBox} from "../../../../atoms/InfoBox";
import {OpenStreetMapsWithPoi} from "../../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useConstructionEndDate} from "../../../../property/hooks/useConstructionEndDate";
import {ViewType} from "../../../../view_type/ViewType";
import {fetchOfferGalleryPictures} from "../../../actions/fetch_gallery_pictures";
import {getOfferTypeName, getOfferTypeNamePlural, getOfferTypeUrl} from "../../../helpers/OfferType";
import {hitGtmLeadGenerator, LeadGeneratorAction} from "../../../tracking/lead_generator_events";
import {OfferType} from "../../../types/OfferType";

interface IProps {
    onCloseClick: () => void;
    onDismissLinkClick: (event: React.MouseEvent<HTMLElement>) => void;
    onRecommendedClick: () => void;
}

export const LeadGeneratorSuccessModalContent = (props: IProps) => {
    const theme = useTheme();
    const additionalAreas = useSelector((store: IRPStore) => store.variables.data?.property_additional_areas);
    const {calculateConstructionEndDate} = useConstructionEndDate();
    const property = useSelector((store: IRPStore) => store.property.property);
    const kitchenType =
        useSelector((store: IRPStore) => store.variables.data?.property_kitchen_type.find(({value}) => value === property?.kitchen_type))?.label ||
        "Brak informacji";

    const hasRecommendations = useSelector((store: IRPStore) => !!store.property.recommendedProperties.length);
    const externalPlanUrl = property?.external_plan_url;

    if (!property) {
        return <></>;
    }

    const noRecommendationsOfferLink = appendQueryString(
        rpAppLink.offer.listFriendly({
            friendlySlug: getOfferTypeUrl(property.offer.type)
        }),
        {region: property.offer.region.id}
    );

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_760x428;
    const {fetchStart: fetchOfferGalleryPicture, response: galleryPictures, fetchState} = useFetch(() => fetchOfferGalleryPictures(property.offer.id));
    useEffect(() => {
        fetchOfferGalleryPicture();
    }, []);
    const gallery = galleryPictures && galleryPictures.map((photo) => photo?.image?.g_img_500).filter((photo) => !!photo);

    const additionalAreasText = property.additional_areas.map(({type}) => additionalAreas?.find(({value}) => value === type)?.label).join(", ") || null;

    return (
        <div css={holder}>
            <div css={greenBox}>
                <Text variant="headline_6">Deweloper wkrótce się z Tobą skontaktuje i przedstawi szczegóły oferty.</Text>
                <Text variant="body_copy_2">
                    Jeśli kliknąłeś przez przypadek lub nie chcesz, aby deweloper się z Tobą kontaktował, odwołaj swoje zgłoszenie.
                </Text>
                <Button type="button" variant="outlined_secondary" size="x-small" onClick={props.onDismissLinkClick} css={{maxWidth: calculateRemSize(25)}}>
                    Odwołaj zgłoszenie
                </Button>
            </div>
            <div css={grayBox}>
                <Text variant="mini_header" css={mb(2)}>
                    Twoje zgłoszenie
                </Text>
                <div css={grayBoxHeader}>
                    <div css={grayBoxHeaderLeft}>
                        <Text variant="headline_4">
                            {`${getOfferTypeName(property.offer.type)} ${property.number}, ${roomsFormat(property.rooms)}, ${areaFormat(property.area)}`}
                        </Text>
                        <Text variant="info_txt_1">{property.offer.address}</Text>
                    </div>
                    <div css={grayBoxHeaderRight}>
                        <img src={property.offer.vendor.logo?.v_log_120x90} alt={`${property.offer.vendor.name} logo`} />
                        <Text variant="info_txt_1" css={mt(1)}>
                            {property.offer.vendor.name}
                        </Text>
                    </div>
                </div>
                <div css={imageWrapper}>
                    {!!planImage && <img src={planImage} alt="plan mieszkania" />}
                    <div css={galleryWrapper}>
                        {fetchState === RequestState.Success && gallery ? (
                            <SliderGallery
                                arrows={{
                                    left: (
                                        <SliderGalleryIcon
                                            position="left"
                                            wrapperColor={theme.colors.primary}
                                            iconSize="1.6"
                                            iconWrapperSize="3.2"
                                            enableOnMobile
                                        />
                                    ),
                                    right: (
                                        <SliderGalleryIcon
                                            position="right"
                                            wrapperColor={theme.colors.primary}
                                            iconSize="1.6"
                                            iconWrapperSize="3.2"
                                            enableOnMobile
                                        />
                                    )
                                }}
                                slides={gallery.map((el, index) => (
                                    <picture key={index}>
                                        <img src={el} alt={`Zdjęcie ${index + 1}`} height="290" width="420" />
                                    </picture>
                                ))}
                                onSlideChange={() => hitGtmLeadGenerator(LeadGeneratorAction.GALLERY_ARROW_CLICK)}
                            />
                        ) : (
                            <CenterBox>
                                <Loader size="lg" />
                            </CenterBox>
                        )}
                    </div>
                </div>
                <div css={pictogramsWrapper}>
                    <InfoBox icon={RoomsIcon} text={property.rooms} title="Ilość pokoi" />
                    <InfoBox icon={AreaIcon} text={areaFormat(property.area as number, {precision: 2})} title="Metraż" />
                    {!!additionalAreasText && <InfoBox icon={AdditionalAreaIcon} text={additionalAreasText} title="Pow. dodatkowe" />}
                    {property.offer.type !== OfferType.COMMERCIAL && <InfoBox icon={KitchenIcon} text={kitchenType} title="Kuchnia" />}
                    <InfoBox
                        icon={CalendarCheckIcon}
                        text={calculateConstructionEndDate(null, property.offer.construction_date_range)}
                        title="Termin oddania"
                    />
                </div>
            </div>
            <div css={mapWrapper}>
                <OpenStreetMapsWithPoi
                    offer={property.offer}
                    viewType={ViewType.PROPERTY}
                    initialPoiTypes={[]}
                    mapConfig={{scrollWheelZoom: false, fitBounds: true}}
                    disablePoiSwitch
                    disableInitiallyOpenedPoiId
                    clusterMarkers
                />
            </div>
            <Text variant="headline_3" css={interestedText}>
                Interesują Cię inne {getOfferTypeNamePlural(property.offer.type)} w tej okolicy?
            </Text>
            <Button
                variant="filled_primary"
                css={mb(4)}
                onClick={() => {
                    if (hasRecommendations) {
                        hitGtmLeadGenerator(LeadGeneratorAction.SHOW_MORE_OFFERS);
                        props.onRecommendedClick();
                    }
                }}
                {...(!hasRecommendations && {
                    as: "a",
                    href: noRecommendationsOfferLink
                })}
            >
                Zobacz podobne oferty
            </Button>
            <Text variant="body_copy_1" css={textAlign("center")}>
                Jeśli powyższa oferta nie spełnia Twoich oczekiwań, <br />
                <a href="" css={underline} onClick={props.onDismissLinkClick}>
                    odwołaj zgodę&nbsp;
                </a>
                na kontakt z deweloperem.
            </Text>
        </div>
    );
};

const holder = css`
    position: relative;
    ${flex("center")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(2)};

    > *:last-child {
        ${pb(5)};
    }
`;

const underline = (theme: Theme) => css`
    text-decoration: underline;
    color: ${theme.colors.gray[800]};
`;

const greenBox = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${p(3)};
    background-color: ${theme.colors.success};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
    width: 100%;
`;

const grayBox = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    background-color: ${theme.colors.gray[100]};
    ${borderRadius(1)};
    width: 100vw;
    ${p(2, 1.5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${w100};
        ${p(2)};
    }
`;

const grayBoxHeader = css`
    ${flex("center", "space-between")};

    img {
        max-width: 80px;
    }
`;

const grayBoxHeaderLeft = css`
    ${flex()};
    ${flexDirection("column")};

    > h4::first-letter {
        text-transform: capitalize;
    }
`;

const grayBoxHeaderRight = css`
    ${flex("center")};
    ${flexDirection("column")};
`;

const pictogramsWrapper = (theme: Theme) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    justify-items: center;
    align-items: center;
    row-gap: ${calculateRemSize(4)};

    ${p(0)};
    gap: ${calculateRemSize(2)};
    > * {
        height: ${calculateRemSize(12)} !important;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${flex("center", "space-between")};
        flex-wrap: wrap;
        ${ph(3)};
        ${pv(2)};
        gap: 2px;

        > * {
            height: initial;
        }
    }
`;

const imageWrapper = (theme: Theme) => css`
    ${flex("center", "center")};
    gap: ${calculateRemSize(2)};
    ${flexDirection("column")};
    ${mt(2)};
    img {
        width: 100%;
        max-width: 420px;
        height: 100%;
        max-height: 260px;
        user-select: none;
    }

    img {
        object-fit: contain;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        width: 856px;
        ${flexDirection("row")};
    }
`;

const galleryWrapper = (theme: Theme) => css`
    display: flex;
    position: relative;
    align-items: center;
    width: 100%;
    max-width: 420px;
    height: 100%;
    max-height: 260px;
    background-color: ${theme.colors.gray[100]};
`;

const mapWrapper = (theme: Theme) => css`
    ${position("relative")};
    ${display("block")};
    width: 100vw;
    height: 444px;
    min-height: 444px;
    overflow: hidden;

    ::after {
        ${position("absolute")};
        top: ${calculateRemSize(1.5)};
        left: ${calculateRemSize(1.5)};
        content: "LOKALIZACJA OFERTY";
        background-color: ${theme.colors.secondary};
        ${ph(1)};
        ${pv(0.5)};
        color: #fff;
        font-size: 14px;
        line-height: 20px;
        ${borderRadius(0.5)};
        letter-spacing: 0.2rem;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${w100};
        ${borderRadius(1)};
    }
`;

const interestedText = css`
    ${mt(1)};
    ${mb(3)};
    text-align: center;
`;
import * as React from "react";
import {useEffect} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {CenterBox} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, display, flex, flexDirection, mb, mt, p, pb, ph, position, pv, textAlign, w100} from "@pg-design/helpers-css";
import {AdditionalAreaIcon, AreaIcon, CalendarCheckIcon, KitchenIcon, RoomsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {SliderGallery, SliderGalleryIcon} from "@pg-design/slider-gallery-module";
import {Text} from "@pg-design/text-module";
import {RequestState, useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, roomsFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../../app/rp_reducer";
import {InfoBox} from "../../../../atoms/InfoBox";
import {OpenStreetMapsWithPoi} from "../../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useConstructionEndDate} from "../../../../property/hooks/useConstructionEndDate";
import {ViewType} from "../../../../view_type/ViewType";
import {fetchOfferGalleryPictures} from "../../../actions/fetch_gallery_pictures";
import {getOfferTypeName, getOfferTypeNamePlural, getOfferTypeUrl} from "../../../helpers/OfferType";
import {hitGtmLeadGenerator, LeadGeneratorAction} from "../../../tracking/lead_generator_events";
import {OfferType} from "../../../types/OfferType";

interface IProps {
    onCloseClick: () => void;
    onDismissLinkClick: (event: React.MouseEvent<HTMLElement>) => void;
    onRecommendedClick: () => void;
}

export const LeadGeneratorSuccessModalContent = (props: IProps) => {
    const theme = useTheme();
    const additionalAreas = useSelector((store: IRPStore) => store.variables.data?.property_additional_areas);
    const {calculateConstructionEndDate} = useConstructionEndDate();
    const property = useSelector((store: IRPStore) => store.property.property);
    const kitchenType =
        useSelector((store: IRPStore) => store.variables.data?.property_kitchen_type.find(({value}) => value === property?.kitchen_type))?.label ||
        "Brak informacji";

    const hasRecommendations = useSelector((store: IRPStore) => !!store.property.recommendedProperties.length);
    const externalPlanUrl = property?.external_plan_url;

    if (!property) {
        return <></>;
    }

    const noRecommendationsOfferLink = appendQueryString(
        rpAppLink.offer.listFriendly({
            friendlySlug: getOfferTypeUrl(property.offer.type)
        }),
        {region: property.offer.region.id}
    );

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_760x428;
    const {fetchStart: fetchOfferGalleryPicture, response: galleryPictures, fetchState} = useFetch(() => fetchOfferGalleryPictures(property.offer.id));
    useEffect(() => {
        fetchOfferGalleryPicture();
    }, []);
    const gallery = galleryPictures && galleryPictures.map((photo) => photo?.image?.g_img_500).filter((photo) => !!photo);

    const additionalAreasText = property.additional_areas.map(({type}) => additionalAreas?.find(({value}) => value === type)?.label).join(", ") || null;

    return (
        <div css={holder}>
            <div css={greenBox}>
                <Text variant="headline_6">Deweloper wkrótce się z Tobą skontaktuje i przedstawi szczegóły oferty.</Text>
                <Text variant="body_copy_2">
                    Jeśli kliknąłeś przez przypadek lub nie chcesz, aby deweloper się z Tobą kontaktował, odwołaj swoje zgłoszenie.
                </Text>
                <Button type="button" variant="outlined_secondary" size="x-small" onClick={props.onDismissLinkClick} css={{maxWidth: calculateRemSize(25)}}>
                    Odwołaj zgłoszenie
                </Button>
            </div>
            <div css={grayBox}>
                <Text variant="mini_header" css={mb(2)}>
                    Twoje zgłoszenie
                </Text>
                <div css={grayBoxHeader}>
                    <div css={grayBoxHeaderLeft}>
                        <Text variant="headline_4">
                            {`${getOfferTypeName(property.offer.type)} ${property.number}, ${roomsFormat(property.rooms)}, ${areaFormat(property.area)}`}
                        </Text>
                        <Text variant="info_txt_1">{property.offer.address}</Text>
                    </div>
                    <div css={grayBoxHeaderRight}>
                        <img src={property.offer.vendor.logo?.v_log_120x90} alt={`${property.offer.vendor.name} logo`} />
                        <Text variant="info_txt_1" css={mt(1)}>
                            {property.offer.vendor.name}
                        </Text>
                    </div>
                </div>
                <div css={imageWrapper}>
                    {!!planImage && <img src={planImage} alt="plan mieszkania" />}
                    <div css={galleryWrapper}>
                        {fetchState === RequestState.Success && gallery ? (
                            <SliderGallery
                                arrows={{
                                    left: (
                                        <SliderGalleryIcon
                                            position="left"
                                            wrapperColor={theme.colors.primary}
                                            iconSize="1.6"
                                            iconWrapperSize="3.2"
                                            enableOnMobile
                                        />
                                    ),
                                    right: (
                                        <SliderGalleryIcon
                                            position="right"
                                            wrapperColor={theme.colors.primary}
                                            iconSize="1.6"
                                            iconWrapperSize="3.2"
                                            enableOnMobile
                                        />
                                    )
                                }}
                                slides={gallery.map((el, index) => (
                                    <picture key={index}>
                                        <img src={el} alt={`Zdjęcie ${index + 1}`} height="290" width="420" />
                                    </picture>
                                ))}
                                onSlideChange={() => hitGtmLeadGenerator(LeadGeneratorAction.GALLERY_ARROW_CLICK)}
                            />
                        ) : (
                            <CenterBox>
                                <Loader size="lg" />
                            </CenterBox>
                        )}
                    </div>
                </div>
                <div css={pictogramsWrapper}>
                    <InfoBox icon={RoomsIcon} text={property.rooms} title="Ilość pokoi" />
                    <InfoBox icon={AreaIcon} text={areaFormat(property.area as number, {precision: 2})} title="Metraż" />
                    {!!additionalAreasText && <InfoBox icon={AdditionalAreaIcon} text={additionalAreasText} title="Pow. dodatkowe" />}
                    {property.offer.type !== OfferType.COMMERCIAL && <InfoBox icon={KitchenIcon} text={kitchenType} title="Kuchnia" />}
                    <InfoBox
                        icon={CalendarCheckIcon}
                        text={calculateConstructionEndDate(null, property.offer.construction_date_range)}
                        title="Termin oddania"
                    />
                </div>
            </div>
            <div css={mapWrapper}>
                <OpenStreetMapsWithPoi
                    offer={property.offer}
                    viewType={ViewType.PROPERTY}
                    initialPoiTypes={[]}
                    mapConfig={{scrollWheelZoom: false, fitBounds: true}}
                    disablePoiSwitch
                    disableInitiallyOpenedPoiId
                    clusterMarkers
                />
            </div>
            <Text variant="headline_3" css={interestedText}>
                Interesują Cię inne {getOfferTypeNamePlural(property.offer.type)} w tej okolicy?
            </Text>
            <Button
                variant="filled_primary"
                css={mb(4)}
                onClick={() => {
                    if (hasRecommendations) {
                        hitGtmLeadGenerator(LeadGeneratorAction.SHOW_MORE_OFFERS);
                        props.onRecommendedClick();
                    }
                }}
                {...(!hasRecommendations && {
                    as: "a",
                    href: noRecommendationsOfferLink
                })}
            >
                Zobacz podobne oferty
            </Button>
            <Text variant="body_copy_1" css={textAlign("center")}>
                Jeśli powyższa oferta nie spełnia Twoich oczekiwań, <br />
                <a href="" css={underline} onClick={props.onDismissLinkClick}>
                    odwołaj zgodę&nbsp;
                </a>
                na kontakt z deweloperem.
            </Text>
        </div>
    );
};

const holder = css`
    position: relative;
    ${flex("center")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(2)};

    > *:last-child {
        ${pb(5)};
    }
`;

const underline = (theme: Theme) => css`
    text-decoration: underline;
    color: ${theme.colors.gray[800]};
`;

const greenBox = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${p(3)};
    background-color: ${theme.colors.success};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
    width: 100%;
`;

const grayBox = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    background-color: ${theme.colors.gray[100]};
    ${borderRadius(1)};
    width: 100vw;
    ${p(2, 1.5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${w100};
        ${p(2)};
    }
`;

const grayBoxHeader = css`
    ${flex("center", "space-between")};

    img {
        max-width: 80px;
    }
`;

const grayBoxHeaderLeft = css`
    ${flex()};
    ${flexDirection("column")};

    > h4::first-letter {
        text-transform: capitalize;
    }
`;

const grayBoxHeaderRight = css`
    ${flex("center")};
    ${flexDirection("column")};
`;

const pictogramsWrapper = (theme: Theme) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    justify-items: center;
    align-items: center;
    row-gap: ${calculateRemSize(4)};

    ${p(0)};
    gap: ${calculateRemSize(2)};
    > * {
        height: ${calculateRemSize(12)} !important;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${flex("center", "space-between")};
        flex-wrap: wrap;
        ${ph(3)};
        ${pv(2)};
        gap: 2px;

        > * {
            height: initial;
        }
    }
`;

const imageWrapper = (theme: Theme) => css`
    ${flex("center", "center")};
    gap: ${calculateRemSize(2)};
    ${flexDirection("column")};
    ${mt(2)};
    img {
        width: 100%;
        max-width: 420px;
        height: 100%;
        max-height: 260px;
        user-select: none;
    }

    img {
        object-fit: contain;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        width: 856px;
        ${flexDirection("row")};
    }
`;

const galleryWrapper = (theme: Theme) => css`
    display: flex;
    position: relative;
    align-items: center;
    width: 100%;
    max-width: 420px;
    height: 100%;
    max-height: 260px;
    background-color: ${theme.colors.gray[100]};
`;

const mapWrapper = (theme: Theme) => css`
    ${position("relative")};
    ${display("block")};
    width: 100vw;
    height: 444px;
    min-height: 444px;
    overflow: hidden;

    ::after {
        ${position("absolute")};
        top: ${calculateRemSize(1.5)};
        left: ${calculateRemSize(1.5)};
        content: "LOKALIZACJA OFERTY";
        background-color: ${theme.colors.secondary};
        ${ph(1)};
        ${pv(0.5)};
        color: #fff;
        font-size: 14px;
        line-height: 20px;
        ${borderRadius(0.5)};
        letter-spacing: 0.2rem;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${w100};
        ${borderRadius(1)};
    }
`;

const interestedText = css`
    ${mt(1)};
    ${mb(3)};
    text-align: center;
`;
